/**
 * D-EZ-109 — THE DISPATCH LINE: THE PAPER'S ODDS FOR NEXT WEEK.
 *
 * ⚠ THE PAPER'S NUMBERS, NOT THE GAME'S. Nothing reads these back. The race is
 * decided by the dice through the printed rules; a 3-1 favourite here has
 * exactly the chance the cards and the dice give him and not a hair more. The
 * Wire says so on the page.
 *
 * ⚠ ONLY HISTORY TO DATE. The line for race N+1 is built from races 1…N (and
 * earlier seasons) and next week's TRACK — the calendar, printed before the
 * year began. `wire.lookahead.test.ts` doctors race N+1 (and everything after)
 * and proves the line does not move.
 *
 * ===========================================================================
 * THE FORMULA
 * ===========================================================================
 *
 *   form    the power rankings' recency-weighted finish score (power.ts),
 *           scaled to 0…1
 *   season  his mean finish score over his starts this year, pulled toward
 *           a prior by two phantom races: last season's mean if he raced then,
 *           else 0.35 (an unknown is a back-marker until he shows otherwise)
 *   track   his mean finish score at next week's track (every season of the
 *           league), pulled toward his season score by two phantom races,
 *           plus 0.06 a win there (up to three)
 *
 *   rating  = 0.45·form + 0.30·season + 0.25·track
 *   chance  = e^(κ·rating) / Σ e^(κ·rating) over everyone eligible (κ = 7)
 *
 * κ was fitted (`wire.measure.test.ts`): over 280 seeded weeks the log-loss
 * is lowest near κ = 7–8 (3.46, against 3.96 for a flat field); at 9 the
 * favourite was overpriced (17% implied, 11% won).
 *
 * The line printed is the fair price (1 − p)/p rounded DOWN to the nearest
 * rung of a bookmaker's ladder — so, like any book, the paper's prices add up
 * to a little more than 100%. Eligible: on the season's roster after race N,
 * not dead, and not on the injury report for race N+1.
 */

import type { HistorySeason, HistoryRace } from '../history/source.ts';
import { allRaces } from '../history/source.ts';
import type { ScheduledRace } from '../schedule.ts';
import { finishScore, POWER_FORMULA } from './power.ts';

export const ODDS_FORMULA = {
  formWeight: 0.45,
  seasonWeight: 0.3,
  trackWeight: 0.25,
  phantomRaces: 2,
  unknownPrior: 0.35,
  trackWinBonus: 0.06,
  trackWinCap: 3,
  kappa: 7,
} as const;

/** A bookmaker's ladder, shortest first: [numerator, denominator]. */
export const ODDS_LADDER: readonly (readonly [number, number])[] = [
  [1, 1], [6, 5], [5, 4], [6, 4], [13, 8], [7, 4], [2, 1], [9, 4], [5, 2], [11, 4], [3, 1], [7, 2], [4, 1],
  [9, 2], [5, 1], [11, 2], [6, 1], [13, 2], [7, 1], [15, 2], [8, 1], [9, 1], [10, 1], [11, 1], [12, 1],
  [14, 1], [16, 1], [18, 1], [20, 1], [22, 1], [25, 1], [28, 1], [30, 1], [33, 1], [40, 1], [50, 1],
  [60, 1], [66, 1], [75, 1], [80, 1], [100, 1], [125, 1], [150, 1], [200, 1], [250, 1], [300, 1], [500, 1],
];

/** The fair price, rounded down to the ladder ("6-1"), and the ladder value. */
export function toLine(p: number): { text: string; value: number } {
  const fair = p >= 1 ? 0 : p <= 0 ? Infinity : (1 - p) / p;
  let pick = ODDS_LADDER[0];
  for (const rung of ODDS_LADDER) {
    if (rung[0] / rung[1] <= fair + 1e-9) pick = rung;
    else break;
  }
  if (fair < 1) {
    // Odds-on: print it the bookmaker's way round ("4-6"), and round it the
    // bookmaker's way too — the payout never above the fair one, so the
    // smallest rung at or past 1/fair.
    const inv = fair > 0 ? 1 / fair : 99;
    const on = ODDS_LADDER.find((rung) => rung[0] / rung[1] >= inv - 1e-9) ?? ODDS_LADDER[ODDS_LADDER.length - 1];
    return { text: `${on[1]}-${on[0]}`, value: on[1] / on[0] };
  }
  // Even money reads "Evens" on any board.
  return { text: pick[0] === pick[1] ? 'Evens' : `${pick[0]}-${pick[1]}`, value: pick[0] / pick[1] };
}

export interface OddsEntry {
  driverId: string;
  name: string;
  rating: number;
  form: number;
  season: number;
  track: number;
  /** The paper's chance he wins, 0…1 (sums to 1 over the field). */
  chance: number;
  line: string;
  /** The line's value as a number (6-1 → 6). */
  lineValue: number;
  /** At next week's track, league history to date. */
  trackStarts: number;
  trackWins: number;
  trackAvg: number | null;
  trackBest: number | null;
  /** League races run at this track to date (the same for everyone). */
  trackRaces: number;
  /** This season, through race N. */
  starts: number;
  wins: number;
  seasonAvg: number | null;
  lastThree: number[];
}

function mean(xs: readonly number[]): number | null {
  return xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : null;
}

function shrink(sum: number, n: number, prior: number): number {
  return (sum + ODDS_FORMULA.phantomRaces * prior) / (n + ODDS_FORMULA.phantomRaces);
}

/**
 * The line for race `next` of season `seasonNumber`, from `seasons` — which
 * must already be cut at race N (`people/context.ts` `cutSeasons`); this
 * function reads every race it is handed. `eligible` is the field: the ids the
 * injury report has not ruled out.
 */
export function oddsFor(
  seasons: readonly HistorySeason[],
  seasonNumber: number,
  next: Pick<ScheduledRace, 'venue' | 'trackId'>,
  eligible: readonly { driverId: string; name: string }[],
  tuning: { kappa?: number } = {},
): OddsEntry[] {
  const races = allRaces(seasons);
  const venue = next.venue ?? next.trackId;
  const thisSeason = races.filter((r) => r.seasonNumber === seasonNumber);
  const lastSeason = races.filter((r) => r.seasonNumber === seasonNumber - 1);
  const window = thisSeason.slice(-POWER_FORMULA.window).reverse();
  const weights = POWER_FORMULA.weights.slice(0, window.length);
  const wsum = weights.reduce((a, b) => a + b, 0);
  const atTrack = races.filter((r) => r.venue === venue);

  const scoreIn = (r: HistoryRace, id: string): { f: number; place: number; won: boolean } | null => {
    const l = r.lines.find((x) => x.driverId === id);
    return l ? { f: finishScore(l.finish, r.lines.length), place: l.finish, won: r.winnerId === id } : null;
  };

  const entries = eligible.map(({ driverId: id, name }) => {
    let form = 0;
    window.forEach((r, k) => {
      const s = scoreIn(r, id);
      if (s) form += weights[k] * (s.f + (s.won ? POWER_FORMULA.winBonus : 0));
    });
    form = wsum > 0 ? form / wsum / (1 + POWER_FORMULA.winBonus) : 0;

    const mine = thisSeason.map((r) => scoreIn(r, id)).filter((s): s is NonNullable<typeof s> => s !== null);
    const last = lastSeason.map((r) => scoreIn(r, id)).filter((s): s is NonNullable<typeof s> => s !== null);
    const prior = mean(last.map((s) => s.f)) ?? ODDS_FORMULA.unknownPrior;
    const season = shrink(mine.reduce((a, s) => a + s.f, 0), mine.length, prior);

    const here = atTrack.map((r) => scoreIn(r, id)).filter((s): s is NonNullable<typeof s> => s !== null);
    const trackWins = here.filter((s) => s.won).length;
    const track =
      shrink(here.reduce((a, s) => a + s.f, 0), here.length, season) + ODDS_FORMULA.trackWinBonus * Math.min(trackWins, ODDS_FORMULA.trackWinCap);

    const rating = ODDS_FORMULA.formWeight * form + ODDS_FORMULA.seasonWeight * season + ODDS_FORMULA.trackWeight * track;
    return {
      driverId: id,
      name,
      rating,
      form,
      season,
      track,
      chance: 0,
      line: '',
      lineValue: 0,
      trackStarts: here.length,
      trackWins,
      trackAvg: here.length ? Math.round((here.reduce((a, s) => a + s.place, 0) / here.length) * 10) / 10 : null,
      trackBest: here.length ? Math.min(...here.map((s) => s.place)) : null,
      trackRaces: atTrack.length,
      seasonAvg: mine.length ? Math.round((mine.reduce((a, s) => a + s.place, 0) / mine.length) * 10) / 10 : null,
      starts: mine.length,
      wins: mine.filter((s) => s.won).length,
      lastThree: thisSeason
        .slice(-3)
        .reverse()
        .map((r) => scoreIn(r, id)?.place)
        .filter((p): p is number => p !== undefined),
    } satisfies OddsEntry;
  });
  const top = Math.max(...entries.map((e) => e.rating), 0);
  const weights2 = entries.map((e) => Math.exp((tuning.kappa ?? ODDS_FORMULA.kappa) * (e.rating - top)));
  const total = weights2.reduce((a, b) => a + b, 0);
  entries.forEach((e, i) => {
    e.chance = total > 0 ? weights2[i] / total : 0;
    const l = toLine(e.chance);
    e.line = l.text;
    e.lineValue = l.value;
  });
  return entries.sort((a, b) => b.chance - a.chance || a.driverId.localeCompare(b.driverId));
}

/** The combined chance of everyone not printed, as one "field" line. */
export function fieldLine(entries: readonly OddsEntry[], shown: number): { chance: number; line: string } | null {
  const rest = entries.slice(shown);
  if (rest.length === 0) return null;
  const chance = rest.reduce((a, e) => a + e.chance, 0);
  return { chance, line: toLine(chance).text };
}
